/**
 * AllToursModal — soft-dismissable overlay that lists every interactive
 * tour and lets admins watch / reset them. Launched from My Access, the
 * AdminPanel footer, and the first-login WelcomeToursPrompt.
 *
 * "Soft" means Escape and outside-click both close the modal (and still
 * record the welcome-prompt as seen for first-login flows).
 */
import { useEffect } from "react";
import { X, BookOpen } from "@phosphor-icons/react";
import AllToursCatalog from "@/components/AllToursCatalog";

export default function AllToursModal({ open, onClose, user }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[800] flex items-center justify-center p-4"
         data-testid="all-tours-modal">
      {/* Backdrop — click to dismiss (soft) */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} data-testid="all-tours-backdrop" />

      <div className="relative w-full max-w-2xl bg-white rounded-md shadow-2xl border border-slate-200 max-h-[88vh] flex flex-col">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-slate-200 flex items-center gap-2">
          <BookOpen size={18} weight="bold" className="text-blue-700" />
          <h2 className="text-base font-bold font-['Chivo']">All interactive tours</h2>
          <button onClick={onClose}
                  data-testid="all-tours-close"
                  className="ml-auto w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
            <X size={16} weight="bold" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 overflow-y-auto">
          <p className="text-xs text-slate-500 mb-3">
            Pick a tour to watch — we'll jump you to that page and start the walkthrough automatically.
          </p>
          <AllToursCatalog user={user} onAfterAction={(action) => { if (action === "watch") onClose?.(); }} />
        </div>
      </div>
    </div>
  );
}
